import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";

/**
 * Breadcrumb bar for Staples Reports.
 * Uses the same sticky behavior as Analytics breadcrumbs to keep consistency.
 */
const StaplesReportsBreadcrumb = () => {
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const { raw_data, userGroupName, userGroupCount } = useSelector(
    (state) => ({
      raw_data: state.audit?.raw_data,
      userGroupName: state.current_user_group?.name,
      userGroupCount: state.current_user_group?.memberCount,
    }),
    shallowEqual
  );

  // Calculate respondent count from the currently selected survey's data
  const getRespondentCount = () => {
    // Get responses from raw_data[0] which contains the selected survey's data
    const responses = raw_data?.[0]?.responses;
    if (Array.isArray(responses) && responses.length > 0) {
      return responses.length;
    }

    // Fallback: try to get from metadata if available
    const metadataCount = raw_data?.[0]?.datasetMetadata?.originalFilteredCount;
    if (metadataCount != null) {
      return metadataCount;
    }

    return null;
  };

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { rootMargin: "0px 0px 0px 0px", threshold: 0 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  // Get respondent count for the currently selected survey
  const respondentCount = getRespondentCount();
  const displayCount = respondentCount != null ? respondentCount : userGroupCount;

  return (
    <>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <BreadcrumbWrapper>
        <BreadcrumbBackdrop isSticky={isSticky} />
        <BreadcrumbContainer>
          <BreadcrumbContent>
            <Trail>
              <BreadcrumbItem onClick={() => navigate("/leader/audit/new-analytics")} clickable>
                Survey Analytics
              </BreadcrumbItem>
              <Separator>/</Separator>
              <BreadcrumbItem active>My Reports</BreadcrumbItem>
            </Trail>
            {userGroupName && (
              <GroupMeta title="Your group">
                <span>
                  {userGroupName}
                  {displayCount != null && ` (${displayCount})`}
                </span>
                <InfoWrapper tabIndex="0" aria-label="Group roll-up info">
                  <InfoIcon aria-hidden="true">i</InfoIcon>
                  <Tooltip role="tooltip">
                    This is the total number of respondents included in your group’s roll-up.
                  </Tooltip>
                </InfoWrapper>
              </GroupMeta>
            )}
          </BreadcrumbContent>
        </BreadcrumbContainer>
      </BreadcrumbWrapper>
    </>
  );
};

export default StaplesReportsBreadcrumb;

const BreadcrumbWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 300;
  margin-bottom: 24px;
  max-width: 100%;
  background: transparent;
  display: block;
  overflow: visible;
`;

const BreadcrumbBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 100vw;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  transform: ${(props) =>
    props.isSticky ? "translate(-50%, 0%)" : "translate(-50%, -100%)"};
  opacity: ${(props) => (props.isSticky ? 1 : 0)};
  transition: transform 0.24s ease, opacity 0.24s ease, box-shadow 0.24s ease;
  box-shadow: ${(props) =>
    props.isSticky ? "0 12px 24px rgba(17, 24, 39, 0.08)" : "none"};
  pointer-events: none;
  z-index: 0;
`;

const StickySentinel = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  margin-top: -1px;
  opacity: 0;
  pointer-events: none;
`;

const BreadcrumbContainer = styled.div`
  background: transparent;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
`;

const BreadcrumbContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;
  max-width: 1200px;
  margin: 0 auto;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const BreadcrumbItem = styled.span`
  color: ${(props) => (props.active ? "#111827" : "#6B7280")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) =>
      props.clickable ? "#374151" : props.active ? "#111827" : "#6B7280"};
  }

  ${(props) =>
    props.clickable &&
    `
    &:hover {
      text-decoration: underline;
      text-decoration-color: #D1D5DB;
      text-underline-offset: 3px;
    }
  `}
`;

const Separator = styled.span`
  margin: 0 8px;
  color: #d1d5db;
  font-weight: 400;
  user-select: none;
`;

const Trail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const GroupMeta = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const InfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  color: #4b5563;
  background: #fff;
  font-size: 12px;
  cursor: default;
  outline: none;

  &:hover > div,
  &:focus > div {
    opacity: 1;
    visibility: visible;
    transform: translate(0, -4px);
  }
`;

const InfoIcon = styled.span`
  line-height: 1;
  font-weight: 700;
`;

const Tooltip = styled.div`
  position: absolute;
  top: -2px;
  right: 0;
  left: auto;
  transform: translate(0, 4px);
  background: #111827;
  color: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: normal;
  text-align: left;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.08s ease, transform 0.08s ease;
  z-index: 2;
  pointer-events: none;
`;
